import { useState, useEffect, useRef, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { Spinner, SpinnerSize } from "@fluentui/react";
import { useMsal } from "@azure/msal-react";
import { useLogin, getToken } from "../../authConfig";
import { useClinicalFilters, defaultClinicalFilters } from "../../clinicalContext";
import { searchPatients, PatientSearchResult } from "../../api";

interface PatientPickerProps {
    variant?: "header" | "modal";
}

export const PatientPicker = ({ variant = "header" }: PatientPickerProps) => {
    const location = useLocation();
    const { instance, accounts, inProgress } = useMsal();
    const isAuthenticated = !useLogin || (accounts.length > 0 && inProgress === "none");
    const client = isAuthenticated ? instance : undefined;
    const account = accounts[0];

    const { filters, setFilters, setPatientInfo, lastUrlPatId, setLastUrlPatId, loadedPatId, setLoadedPatId } = useClinicalFilters();

    const [results, setResults] = useState<PatientSearchResult[]>([]);
    const [isSearching, setIsSearching] = useState(false);
    const [inputValue, setInputValue] = useState(filters.patientName || "");
    const [showDropdown, setShowDropdown] = useState(false);
    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const containerRef = useRef<HTMLDivElement | null>(null);

    const getIdToken = async () => {
        if (useLogin && client && account) {
            client.setActiveAccount(account);
            return (await getToken(client)) ?? undefined;
        }
        return undefined;
    };

    const handleSearch = useCallback(
        async (query: string) => {
            if (query.length < 2) {
                setResults([]);
                setShowDropdown(false);
                return;
            }
            setIsSearching(true);
            try {
                const token = await getIdToken();
                const res = await searchPatients(query, token);
                setResults(res);
                setShowDropdown(res.length > 0);
            } catch (error) {
                console.error("Patient search failed:", error);
                setResults([]);
                setShowDropdown(false);
            } finally {
                setIsSearching(false);
            }
        },
        [client, account]
    );

    const debouncedSearch = useCallback(
        (val: string) => {
            if (debounceRef.current) clearTimeout(debounceRef.current);
            debounceRef.current = setTimeout(() => handleSearch(val), 300);
        },
        [handleSearch]
    );

    const handleSelect = (patient: PatientSearchResult) => {
        setInputValue(patient.patientName);
        setShowDropdown(false);
        setResults([]);
        setFilters({
            ...defaultClinicalFilters,
            patId: patient.patId,
            patientName: patient.patientName
        });
        setPatientInfo(null);
        setLastUrlPatId(patient.patId);
        setLoadedPatId(patient.patId);
    };

    // Sync input display when patient changes externally (e.g. URL param)
    useEffect(() => {
        setInputValue(filters.patientName || "");
    }, [filters.patientName]);

    // Handle URL-based patient selection
    useEffect(() => {
        if (lastUrlPatId && lastUrlPatId !== loadedPatId) {
            setFilters(prev => ({
                ...defaultClinicalFilters,
                patId: lastUrlPatId,
                patientName: prev.patientName
            }));
            setLoadedPatId(lastUrlPatId);
        }
    }, [lastUrlPatId]);

    // Close dropdown on outside click
    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
                setShowDropdown(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const isModal = variant === "modal";

    // Show login prompt when auth is required but user hasn't logged in
    if (useLogin && !isAuthenticated) {
        return <span style={{ color: isModal ? "#666" : "#888", fontSize: 13, whiteSpace: "nowrap" }}>Please login →</span>;
    }

    return (
        <div ref={containerRef} style={{ position: "relative", width: isModal ? "100%" : 220 }}>
            <div style={{ position: "relative" }}>
                <input
                    type="text"
                    placeholder="Search patient..."
                    value={inputValue}
                    onChange={e => {
                        setInputValue(e.target.value);
                        debouncedSearch(e.target.value);
                    }}
                    onFocus={() => {
                        if (results.length > 0) setShowDropdown(true);
                    }}
                    style={{
                        width: "100%",
                        padding: "6px 30px 6px 10px",
                        fontSize: 16,
                        fontWeight: 600,
                        border: isModal ? "1px solid #999" : "1px solid #555",
                        borderRadius: isModal ? 4 : 2,
                        backgroundColor: isModal ? "#fff" : "transparent",
                        color: isModal ? "#333" : "#fff",
                        outline: "none",
                        boxSizing: "border-box"
                    }}
                />
                {isSearching && (
                    <div style={{ position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)" }}>
                        <Spinner size={SpinnerSize.xSmall} />
                    </div>
                )}
            </div>
            {showDropdown && results.length > 0 && (
                <ul
                    style={{
                        position: "absolute",
                        top: "100%",
                        left: 0,
                        right: 0,
                        margin: 0,
                        padding: 0,
                        listStyle: "none",
                        backgroundColor: "#fff",
                        border: "1px solid #ccc",
                        borderRadius: 4,
                        boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                        zIndex: 10000,
                        maxHeight: 250,
                        overflowY: "auto"
                    }}
                >
                    {results.map(r => (
                        <li
                            key={r.patId}
                            onClick={() => handleSelect(r)}
                            style={{
                                padding: "8px 12px",
                                cursor: "pointer",
                                fontSize: 14,
                                color: "#333",
                                borderBottom: "1px solid #eee"
                            }}
                            onMouseEnter={e => (e.currentTarget.style.backgroundColor = "#f0f0f0")}
                            onMouseLeave={e => (e.currentTarget.style.backgroundColor = "#fff")}
                        >
                            {r.patientName}
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
};
